import { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  AgxRadio,
  AgxDatePicker,
  AgxLabel,
  AustralianState,
  AgxCheckbox,
} from '@urbanx/agx-ui-components';
import { LengthOfAgreement } from './LengthOfAgreement';
import { AgreementDetails, AgreementLength, AgreementType } from './types';
import { getEndDateForAgreementLength } from './agreementDateHelpers';
import './AgreementDetails.scss';

interface AgreementDetailsProps {
  id: string;
  onValueChanged: (value: { id: string; value: AgreementDetails }) => void;
  defaultValue: AgreementDetails;
  validate?: boolean;
  state?: string;
  days?: number[];
  restrictDuration?: boolean;
}

const AgreementDetailsComponent = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  state,
  days,
  restrictDuration,
}: AgreementDetailsProps) => {
  const [agreementType, setAgreementType] = useState<
    AgreementType | undefined
  >();

  useEffect(() => {
    const defaultVal = defaultValue?.agreementType ?? AgreementType.Exclusive;
    setAgreementType(defaultVal);
  }, []);

  const nswSpecific = state === AustralianState.NSW;

  const defaultStartDate =
    (defaultValue?.startDate ? dayjs(defaultValue.startDate) : undefined) ??
    dayjs();

  const getDefaultAgreementLength = (
    startDate: Dayjs,
    endDate?: Dayjs
  ): AgreementLength | undefined => {
    if (!startDate) return undefined;
    if (!endDate) return undefined;

    const endDateComparison = endDate.format('YYYY-MM-DD');

    if (
      endDateComparison ===
      getEndDateForAgreementLength(startDate, 30)
    )
      return AgreementLength.ThirtyDays;
    if (
      endDateComparison ===
      getEndDateForAgreementLength(startDate, 60)
    )
      return AgreementLength.SixtyDays;
    if (
      endDateComparison ===
      getEndDateForAgreementLength(startDate, 90)
    )
      return AgreementLength.NinetyDays;
    if (
      endDateComparison ===
      getEndDateForAgreementLength(startDate, 120)
    )
      return AgreementLength.OneHundredTwentyDays;

    return AgreementLength.SpecificDate;
  };

  const [startDate, setStartDate] = useState(
    defaultStartDate.format('YYYY-MM-DD')
  );

  const [agreementLength, setAgreementLength] = useState<string | undefined>(
    getDefaultAgreementLength(
      defaultStartDate,
      defaultValue?.endDate ? dayjs(defaultValue.endDate) : undefined
    )
  );

  const [specificEndDate, setSpecificEndDate] = useState(
    agreementLength === 'specificDate' && defaultValue?.endDate
      ? defaultValue.endDate
      : undefined
  );

  const [
    continueToOpenListingOnExpiration,
    setContinueToOpenListingOnExpiration,
  ] = useState(defaultValue?.continueToOpenListingOnExpiration ?? true);

  const calculateEndDate = (
    startDate: string,
    lengthOfAgreement?: string | AgreementLength,
    agreementType?: AgreementType
  ) => {
    if (startDate == null) return null;
    if (agreementType === AgreementType.Open) return null;
    if (lengthOfAgreement === AgreementLength.SpecificDate)
      return specificEndDate;

    var startDateVal = dayjs(startDate, 'YYYY-MM-DD');

    switch (lengthOfAgreement) {
      case AgreementLength.ThirtyDays:
        return getEndDateForAgreementLength(startDateVal, 30);
      case AgreementLength.SixtyDays:
        return getEndDateForAgreementLength(startDateVal, 60);
      case AgreementLength.NinetyDays:
        return getEndDateForAgreementLength(startDateVal, 90);
      case AgreementLength.OneHundredTwentyDays:
        return getEndDateForAgreementLength(startDateVal, 120);
      default:
        return null;
    }
  };

  useEffect(() => {
    var agreementDetails = {
      agreementType,
      startDate: dayjs(startDate, 'YYYY-MM-DD').format('YYYY-MM-DD'),
      endDate: calculateEndDate(startDate, agreementLength),
      continueToOpenListingOnExpiration,
    } as AgreementDetails;

    onValueChanged({ id, value: agreementDetails });
  }, [
    agreementType,
    startDate,
    agreementLength,
    specificEndDate,
    continueToOpenListingOnExpiration,
  ]);

  const startDatePicker = (
    <AgxDatePicker
      key="ListingDetailsAgreementExclusiveStartDate"
      id="ListingDetailsAgreementExclusiveStartDate"
      onValueChanged={({ value }) => setStartDate(value)}
      required
      preFill
      label="Start Date"
      defaultValue={startDate}
      date
      stretch
      error={
        validate && (!startDate || startDate === 'Invalid date')
          ? 'Start Date must be completed'
          : undefined
      }
    />
  );

  const lengthOfAgreement = (
    <LengthOfAgreement
      key="ListingDetailsAgreementLength"
      specificDateChange={value => setSpecificEndDate(value)}
      onLengthOfAgreementChange={value => setAgreementLength(value)}
      defaultLengthOfAgreement={agreementLength}
      defaultSpecificDate={specificEndDate}
      days={days}
      validate={validate}
      restrictDuration={restrictDuration}
      startDate={startDate}
    />
  );

  const continueToOpenListingElem = (
    <AgxCheckbox
      key="continueToOpenListingOnExpiration"
      id="continueToOpenListingOnExpiration"
      subLabel="Continue as an open listing upon expiration"
      onValueChanged={({ value }) => setContinueToOpenListingOnExpiration(value)}
      required
      defaultValue={continueToOpenListingOnExpiration}
    />
  );

  return (
    <div className="agxRadio">
      <AgxLabel medium>Agreement Type</AgxLabel>
      <div className="agxRadio-group">
        <AgxRadio
          id={AgreementType.Exclusive}
          label={AgreementType.Exclusive}
          onCheckChanged={() => setAgreementType(AgreementType.Exclusive)}
          checked={agreementType === AgreementType.Exclusive}
          subContent={
            agreementType === AgreementType.Exclusive
              ? [
                  startDatePicker,
                  lengthOfAgreement,
                  !nswSpecific && continueToOpenListingElem,
                ]
              : undefined
          }
        />
        {!nswSpecific && (
          <>
            <AgxRadio
              id={AgreementType.Open}
              label={AgreementType.Open}
              onCheckChanged={() => setAgreementType(AgreementType.Open)}
              checked={agreementType === AgreementType.Open}
              subContent={
                agreementType === AgreementType.Open
                  ? [startDatePicker]
                  : undefined
              }
            />
            <AgxRadio
              id={AgreementType.Sole}
              label={AgreementType.Sole}
              onCheckChanged={() => setAgreementType(AgreementType.Sole)}
              checked={agreementType === AgreementType.Sole}
              subContent={
                agreementType === AgreementType.Sole
                  ? [
                      startDatePicker,
                      lengthOfAgreement,
                      continueToOpenListingElem,
                    ]
                  : undefined
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AgreementDetailsComponent;
